import { GridList, GridListItem } from 'react-aria-components';
import { Trans, useLingui } from '@lingui/react/macro';

import { ArticleNameCell } from '@/components/ArticlesTableCells';
import BaseMoneyCell, {
  ArticleOrganizationPriceCardItem,
  ArticleWorkshopPriceCardItem,
} from '@/components/ArticlesTableCells/ArticleMoneyCell/ArticleMoneyCell';
import {
  ArticleServiceTypeCardItem,
  ArticleServiceTypeCell,
} from '@/components/ArticlesTableCells/ArticleServiceTypeCell';
import { Card, CardContent, CardItems } from '@/components/Card/Card';
import { useNavigate } from '@/components/Link/Link';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import IconChevron from '@/icons/Chevron.svg';
import { hasActions, useArticleName } from '@/models/article';
import { ArticleWithRelations } from '@/models/request';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { createBEMClasses } from '@/utils/classname';
import { shouldShowPendingOrganizationQuote, shouldShowPendingWorkshopQuote } from '@/utils/price';

import './CompletedArticlesTable.css';

const { block, element } = createBEMClasses('completed-articles-table');

export const CompletedArticlesTable = ({ articles }: { articles: ArticleWithRelations[] }) => {
  const { t } = useLingui();
  const {
    view: { workshopPrice, organizationPrice },
  } = useRequestContext();

  return (
    <Table
      ariaLabel={t({ id: 'request.articles.table.label', message: 'Items' })}
      columnWidths={[
        'minmax(200px, 1fr)',
        'minmax(120px, 1fr)',
        workshopPrice.shown && 'minmax(120px, 1fr)',
        organizationPrice.shown && 'minmax(auto, 150px)',
      ]}
      className={block()}
    >
      <CompletedArticlesTableHeader />
      <Body>
        {articles.map((article) => (
          <CompletedArticlesTableRow key={article.id} article={article} />
        ))}
      </Body>
    </Table>
  );
};

const CompletedArticlesTableHeader = () => {
  const {
    view: { workshopPrice, organizationPrice },
  } = useRequestContext();

  return (
    <Header>
      <Row>
        <Column>
          <Trans id="request.articles.table.label">Items</Trans>
        </Column>
        <Column>
          <Trans id="request.articles.table.column.service-type.label">Service Type</Trans>
        </Column>
        {workshopPrice.shown && (
          <Column justifyContent="flex-end" textAlign="right">
            {workshopPrice.label}
          </Column>
        )}
        {organizationPrice.shown && (
          <Column justifyContent="flex-end" textAlign="right">
            {organizationPrice.label}
          </Column>
        )}
      </Row>
    </Header>
  );
};

const CompletedArticlesTableRow = ({ article }: { article: ArticleWithRelations }) => {
  const {
    view: { workshopPrice, organizationPrice },
    request,
  } = useRequestContext();

  const articleHasBeenExchanged = request.allArticles.some((a) => a.exchangedFromId === article.id);

  return (
    <Row to={`/requests/${article.requestId}/articles/${article.id}`}>
      <Cell isLink>
        <ArticleNameCell article={article} />
      </Cell>
      <Cell>
        <ArticleServiceTypeCell article={article} />
      </Cell>
      {workshopPrice.shown && (
        <Cell justifyContent="flex-end">
          {!articleHasBeenExchanged ? (
            <BaseMoneyCell
              price={article.snapshot.workshopPrice?.amountPerCurrency?.[0]}
              showAmountBeforeTaxes={workshopPrice.showAmountBeforeTaxes}
              emptyLabel={shouldShowPendingWorkshopQuote(article) ? undefined : '-'}
            />
          ) : (
            <>-</>
          )}
        </Cell>
      )}
      {organizationPrice.shown && (
        <Cell justifyContent="flex-end">
          {!articleHasBeenExchanged ? (
            <BaseMoneyCell
              price={article.snapshot.organizationPrice?.amountPerCurrency?.[0]}
              showAmountBeforeTaxes={organizationPrice.showAmountBeforeTaxes}
              emptyLabel={shouldShowPendingOrganizationQuote(article) ? undefined : '-'}
            />
          ) : (
            <>-</>
          )}
        </Cell>
      )}
    </Row>
  );
};

export const CompletedArticlesCardList = ({ articles }: { articles: ArticleWithRelations[] }) => {
  const navigate = useNavigate();
  const { request } = useRequestContext();
  const { t } = useLingui();

  return (
    <GridList
      className={block()}
      onAction={(key) => {
        navigate(`/requests/${request.id}/articles/${key}`);
      }}
      aria-label={t({
        id: 'request.articles.table.waiting-tasks.label',
        message: 'Waiting for others',
      })}
    >
      {articles.map((article) => (
        <ArticleCard key={article.id} article={article} />
      ))}
    </GridList>
  );
};

const ArticleCard = ({ article }: { article: ArticleWithRelations }) => {
  const {
    view: { workshopPrice, organizationPrice },
  } = useRequestContext();

  const articleName = useArticleName({ article });

  return (
    <GridListItem id={article.id} textValue={articleName} className={element('item')}>
      <Card>
        <CardContent>
          <Stack
            row
            gap="0.5rem"
            flexWrap="nowrap"
            justifyContent="space-between"
            alignItems="center"
          >
            <ArticleNameCell id={article.id} article={article} />
            <IconChevron right style={{ fontSize: '1.5rem' }} />
          </Stack>
          <CardItems>
            {hasActions(article) && <ArticleServiceTypeCardItem article={article} />}
            {workshopPrice.shown && <ArticleWorkshopPriceCardItem article={article} />}
            {organizationPrice.shown && <ArticleOrganizationPriceCardItem article={article} />}
          </CardItems>
        </CardContent>
      </Card>
    </GridListItem>
  );
};
